import { useState, useEffect } from 'react'
import { type MRT_PaginationState } from 'material-react-table'
import { getApprovedSubmissions, type IApprovedSubmissionExpanded, deleteApprovedSubmission, searchApprovedSubmissions } from '../../../pocketbase/PocketbaseApprovedSubmissionsService'
import { ConfirmEditSubmissionDialog } from '../../../components/dialogs/ConfirmEditSubmissionDialog'

import { pb } from '../../../pocketbase/Pocketbase'
import { ApprovedSubmissionsTable, type IApprovedSubmissionWithMetadata } from './ApprovedSubmissionsTable'
import { ConfirmationDialog } from '../../../components/dialogs/ConfirmationDialog'
import { useNavigate } from 'react-router-dom'

export const ApprovedSubmissionsView = () => {
  const navigate = useNavigate()

  const [submissions, setSubmissions] = useState<IApprovedSubmissionWithMetadata[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [pendingEditSubmission, setPendingEditSubmission] = useState<IApprovedSubmissionWithMetadata | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [pendingDeleteSubmission, setPendingDeleteSubmission] = useState<IApprovedSubmissionWithMetadata | null>(null)
  const [searchValue, setSearchValue] = useState('')

  // Reset to first page when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchValue])

  // Load submissions when pagination or search changes
  useEffect(() => {
    loadSubmissions()
  }, [pagination.pageIndex, pagination.pageSize, searchValue])

  const convertToMetadata = (submission: IApprovedSubmissionExpanded): IApprovedSubmissionWithMetadata => {
    // Get image URL if image exists
    let imgUrl: string | null = null
    if (submission.image) {
      imgUrl = pb.files.getURL(submission, submission.image)
    }

    // Use expanded relations directly from PocketBase response
    return {
      id: submission.id,
      name: submission.name,
      slug: submission.slug || '',
      imgUrl,
      description: submission.description || '',
      year: submission.year || null,
      brand: submission.expand?.brand,
      country: submission.expand?.country,
      perfumers: submission.expand?.perfumers || [],
      price: submission.expand?.price,
      topNotes: submission.expand?.top_notes || [],
      middleNotes: submission.expand?.middle_notes || [],
      bottomNotes: submission.expand?.bottom_notes || [],
      submitted_by: submission.submitted_by,
      created: submission.created,
    }
  }

  const loadSubmissions = async () => {
    try {
      setIsLoading(true)

      // Pocketbase uses 1-indexed pages
      const page = pagination.pageIndex + 1
      const search = searchValue ? searchValue.trim() : undefined

      const result = search
        ? await searchApprovedSubmissions(page, pagination.pageSize, search)
        : await getApprovedSubmissions(page, pagination.pageSize)

      // Convert to metadata format using expanded relations
      const withMetadata = result.items.map(convertToMetadata)
      setSubmissions(withMetadata)
      setRowCount(result.totalItems)
    } catch (error) {
      console.error('Error loading approved submissions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleView = (submission: IApprovedSubmissionWithMetadata) => {
    navigate(`/fragrance/${submission.slug}`)
  }

  const handleEdit = (submission: IApprovedSubmissionWithMetadata) => {
    setPendingEditSubmission(submission)
    setEditDialogOpen(true)
  }

  const handleDelete = (submission: IApprovedSubmissionWithMetadata) => {
    setPendingDeleteSubmission(submission)
    setDeleteDialogOpen(true)
  }



  const confirmDelete = async () => {
    if (!pendingDeleteSubmission) {
      return
    }

    try {
      await deleteApprovedSubmission(pendingDeleteSubmission.id)
      setDeleteDialogOpen(false)
      setPendingDeleteSubmission(null)
      // Reload submissions to reflect the deletion
      await loadSubmissions()
    } catch (error) {
      console.error('Error deleting submission:', error)
      alert('Failed to delete submission. Please try again.')
    }
  }

  return (
    <div className={'flex flex-col h-full'}>
      {/* Fixed Header */}
      <div
        className={
          'flex items-center justify-between px-8 pb-6 pt-8 border-searchBorder'
        }
      >
        <h1 className={'text-2xl font-bold'}>
          Approved Submissions
        </h1>
      </div>

      {/* Material React Table */}
      <div className={'flex-1 overflow-hidden'}>
        <ApprovedSubmissionsTable
          data={submissions}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
      </div>

      <ConfirmEditSubmissionDialog
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        submissionId={pendingEditSubmission?.id || null}
      />

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Approved Submission"
        content={`Are you sure you want to delete "${pendingDeleteSubmission?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  )
}
